import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Drawer from "@material-ui/core/Drawer";
import Button from "@material-ui/core/Button";
import SimpleInput from "../../components/Frequents/SimpleInput";
import SimpleReadOnly from "../../components/Frequents/SimpleReadOnly";
import Clock from "../Frequents/TimePicker.js";
import { AppBar, Toolbar, Typography, FormControl, Grid, InputLabel, Select, Link, MenuItem } from "@material-ui/core";
import Container from "@material-ui/core/Container";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import API from "../../actions";
import update from "immutability-helper";
import moment from "moment";
import { getDKDateTimeRange, isNotDKTimeZone } from "../../libs/dktime";
import { useCallback } from "react";
import { KeyboardDatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";

const useStyles = makeStyles({
    list: {
        width: 250
    },
    fullList: {
        width: "auto"
    },
    appBar: { backgroundColor: "#fff", color: "#000" },
    paper: {
        height: "100%"
    },
    birthdayInput: {
        "& input": {
            minHeight: "24px !important"
        }
    }
});

function getDayTimestamps(date) {
    const now = new Date(date);

    // Start of the day (00:00:00)
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startTimestamp = startOfDay.getTime();

    // End of the day (23:59:59.999)
    const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
    const endTimestamp = endOfDay.getTime();

    return {
        startTimestamp,
        endTimestamp
    };
}

const EditBookingDrawer = props => {
    const classes = useStyles();

    const [error, setError] = React.useState("");
    const [treatmentArray, setTreatmentArray] = useState([]);
    const [newClient, setNewClient] = useState(false);
    const [booking, setBooking] = useState({});
    const [time, setTime] = useState(null);
    const [toTime, setToTime] = useState(null);
    const [duration, setDuration] = useState(0);
    const [dateValue, setDateValue] = useState();
    const [selectedTreatment, setSelectedTreatment] = useState(null);

    const [leave, setLeave] = useState(false);

    const dayStart = moment(props?.dateInfo?.data?.start).format("YYYY-MM-DDT");

    const showError = useCallback(msg => setError(msg), []);

    const isOverlapping = useCallback((start, end, bookings) => bookings.some(b => start < b.to && end > b.from), []);

    useEffect(() => {
        (async () => {
            if (newClient) {
                setTreatmentArray(await API.getAvailableTreatmentsFromTherapist());
            } else {
                setTreatmentArray(await API.getTreatmentsFromTherapist());
            }
        })();
    }, [newClient]);

    useEffect(() => {
        if (props.dateInfo && props.isEdit) {
            const data = props.dateInfo.clientData;

            setTime(moment(props?.dateInfo?.data?.start).format("HH:mm:ss"));
            setToTime(moment(props?.dateInfo?.data?.end).format("HH:mm:ss"));

            const treatment = treatmentArray.find(t => t.id === props.dateInfo.booking.treatmentId);

            setBooking({
                clientId: data.id || "",
                nameFirst: data.nameFirst || "",
                nameLast: data.nameLast || "",
                CPR: data.CPR || "",
                CPR6Digit: data.CPR6Digit || "",
                CPRLast4Digit: data.CPRLast4Digit || "",
                email: data.email || "",
                phonePrivate: data.phonePrivate || "",
                treatmentId: props.dateInfo.data.treatmentId || "",
                reason: props.dateInfo.data.reason || ""
            });

            setDuration(treatment?.duration || 0);
            setSelectedTreatment(treatment);
            setNewClient(false);
            setLeave(props.dateInfo.data.leave || false);
            const bookingDate = moment.utc(props.dateInfo.data.start).toDate();

            setDateValue(bookingDate);
        }
    }, [props.dateInfo, props.isEdit, treatmentArray]);

    const saveBooking = useCallback(async () => {
        try {
            // Merge dateValue + time into one moment object
            const [hours, minutes, seconds] = time.split(":").map(Number);

            const mergedStart = moment(dateValue).set({ hour: hours, minute: minutes, second: seconds || 0 });

            // const mergedStart = moment(dateValue).set({
            //     hour: Number(time.split(":")[0]),
            //     minute: Number(time.split(":")[1]),
            //     second: Number(time.split(":")[2]) || 0
            // });

            const startTimestamp = mergedStart.valueOf();
            const endTimestamp = startTimestamp + duration * 1000;

            const leaves = await API.getLeavesForDay({
                from: startTimestamp,
                to: endTimestamp
            });
            const bookings = leaves.map(b => ({ from: +b.from, to: +b.to }));

            if (isOverlapping(startTimestamp, endTimestamp, bookings)) {
                showError("Denne tidspunkt overlapper med en anden booking. Vælg en anden tidspunkt.");
                return;
            }

            if (!leave) {
                const mailFormat = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
                const phoneFormat = /^\+*\d{8,}$/;
                booking.CPR = booking.CPR6Digit + booking.CPRLast4Digit;

                if (newClient) {
                    if (!booking.nameFirst) return showError("Fejl: Fornavn er påkrævet.");
                    if (!booking.nameLast) return showError("Fejl: Efternavn er påkrævet.");
                    if ((booking.CPR6Digit || "").length !== 6) return showError("Fejl: Fødselsdag skal være 6 cifre.");
                    if ((booking.CPRLast4Digit || "").length !== 4)
                        return showError("Fejl: De sidste 4 cifre i dit CPR nr skal bestå af 4 tegn.");
                    if (!booking.email || !booking.email.match(mailFormat))
                        return showError("Fejl: Invalid email adresse");
                    if (!booking.phonePrivate || !booking.phonePrivate.replace(/\s/g, "").match(phoneFormat))
                        return showError(
                            "Fejl: Telefonnummer skal være mindst 8 cifre og må ikke indeholde specialtegn udover +"
                        );
                }

                if (!booking.treatmentId) return showError("Fejl: Behandling er påkrævet.");

                const b = update(booking, {
                    from: { $set: startTimestamp },
                    to: { $set: endTimestamp },
                    id: { $set: props.dateInfo.booking.id }
                });

                await API.setBooking(b);
                props.close();
            } else {
                if (!time) return showError("Fejl: Tid er påkrævet.");
                if (!toTime) return showError("Fejl: Tid er påkrævet.");
                if (!booking.reason) return showError("Fejl: Afsnitt er påkrævet.");

                await API.addLeave({
                    reason: booking.reason,
                    from: moment(dayStart + time).valueOf(),
                    to: moment(dayStart + toTime).valueOf()
                });
                props.close();
            }

            const calendarApi = props.calendarRef.current?.getApi();
            if (calendarApi && props.currentViewDate) {
                calendarApi.gotoDate(props.currentViewDate);
            }
        } catch (err) {
            showError(err.toString());
        }
    }, [booking, time, toTime, duration, leave, newClient, dateValue]);

    return (
        <>
            <Drawer anchor="bottom" open={Boolean(props.dateInfo)} onClose={props.close}>
                <AppBar position="static" className={classes.appBar}>
                    <Toolbar>
                        <IconButton
                            edge="start"
                            className={classes.menuButton}
                            color="inherit"
                            aria-label="menu"
                            onClick={props.close}>
                            <CloseIcon />
                        </IconButton>
                        <Typography variant="h6" className={classes.title}>
                            Rediger reservation
                        </Typography>
                    </Toolbar>
                </AppBar>

                <Container maxWidth="md" style={{ height: "100%", paddingTop: "20px" }}>
                    {!leave && (
                        <>
                            <Grid container spacing={0}>
                                <Grid item sm={3} style={{ width: "100%" }}>
                                    <SimpleReadOnly text="Fornavn" prop={booking.nameFirst ? booking.nameFirst : ""} />
                                </Grid>
                                <Grid item sm={3} style={{ width: "100%" }}>
                                    <SimpleReadOnly
                                        style={{ flex: 1 }}
                                        text="Efternavn"
                                        prop={booking.nameLast ? booking.nameLast : ""}
                                    />
                                </Grid>
                                <Grid item sm={3} style={{ width: "100%" }}>
                                    <SimpleReadOnly
                                        style={{ flex: 1 }}
                                        text="Fødselsdag"
                                        prop={booking.CPR6Digit ? booking.CPR6Digit : ""}
                                    />
                                </Grid>
                                <Grid item sm={3} style={{ width: "100%" }}>
                                    <SimpleReadOnly
                                        style={{ flex: 1 }}
                                        text="De sidste 4 cifre i dit CPR nr"
                                        prop={booking.CPRLast4Digit ? booking.CPRLast4Digit : ""}
                                    />
                                </Grid>
                                <Grid item sm={6} style={{ width: "100%" }}>
                                    <SimpleReadOnly text="Email" prop={booking.email ? booking.email : ""} />
                                </Grid>
                                <Grid item sm={6} style={{ width: "100%" }}>
                                    <SimpleReadOnly
                                        text="Telefonnummer"
                                        prop={booking.phonePrivate ? booking.phonePrivate : ""}
                                    />
                                </Grid>
                            </Grid>
                        </>
                    )}
                    {!leave && (
                        <div style={{ width: "100%", margin: "10px auto 10px auto" }}>
                            <FormControl style={{ width: "100%" }} required>
                                {treatmentArray && treatmentArray.length === 0 ? (
                                    <>
                                        <InputLabel id="demo-simple-select-label">Behandling</InputLabel>
                                        <Select labelId="newTreatmentSelect" id="newTreatmentSelect">
                                            <Link
                                                key="childkey"
                                                style={{ width: "100%" }}
                                                to={{
                                                    pathname: "/Treatments"
                                                }}>
                                                <MenuItem>Ny behandling</MenuItem>
                                            </Link>
                                        </Select>
                                    </>
                                ) : (
                                    <Select
                                        labelId="treatmentSelect"
                                        id="treatmentSelect"
                                        name="treatmentSelect"
                                        value={selectedTreatment ? selectedTreatment.id : ""}
                                        onChange={async event => {
                                            const value = event.target.value;
                                            let treatment;

                                            if (props.isEdit) {
                                                treatment = treatmentArray?.find(t => String(t.id) === String(value));
                                            } else {
                                                treatment = await API.getTreatment(value);
                                            }

                                            if (treatment) {
                                                setBooking(prev => ({
                                                    ...prev,
                                                    treatmentId: treatment.id
                                                }));
                                                setDuration(treatment.duration);
                                            }
                                        }}
                                        data-testid="treatment-selector">
                                        {treatmentArray.map(treatment => (
                                            <MenuItem key={treatment.id} value={treatment.id}>
                                                {treatment.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                )}
                            </FormControl>
                        </div>
                    )}
                    <Grid container spacing={0}>
                        <Grid item sm={4} style={{ width: "100%" }}>
                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                <KeyboardDatePicker
                                    style={{ width: "100%" }}
                                    className={classes.birthdayInput}
                                    autoOk
                                    variant="inline"
                                    format="dd/MM/yyyy"
                                    margin="normal"
                                    label="Fødselsdag (DD/MM/YYYY)"
                                    value={dateValue}
                                    onChange={date => {
                                        setDateValue(date);
                                    }}
                                    KeyboardButtonProps={{
                                        "aria-label": "change date"
                                    }}
                                />
                            </MuiPickersUtilsProvider>
                        </Grid>
                        <Grid item sm={4} style={{ width: "100%" }}>
                            <Clock
                                id="newBookingFrom"
                                style={{ flex: 1 }}
                                value={time}
                                changeValue={setTime}
                                text="fra"
                            />
                        </Grid>
                        {leave && (
                            <Grid item sm={4} style={{ width: "100%" }}>
                                <Clock
                                    id="newBookingFrom"
                                    style={{ flex: 1 }}
                                    value={toTime}
                                    changeValue={setToTime}
                                    text="til"
                                />
                            </Grid>
                        )}
                        {!leave && (
                            <Grid item sm={4} style={{ width: "100%" }}>
                                <SimpleReadOnly
                                    id="newBookingTo"
                                    style={{ flex: 1 }}
                                    name="newBookingTo"
                                    text="Slut"
                                    prop={
                                        time
                                            ? moment(
                                                  new Date(Number(Date.parse("1970-01-01T" + time) + 1000 * duration))
                                              ).format("HH:mm:ss")
                                            : ""
                                    }
                                />
                            </Grid>
                        )}
                    </Grid>
                    {isNotDKTimeZone() && (
                        <div class="dk-time">
                            Du er ikke DK-tidszone, dette er tid i DK-tidszone:{" "}
                            {getDKDateTimeRange(
                                moment(moment(props.dateInfo.data.time).format("YYYY-MM-DDT") + time).valueOf(),
                                moment(moment(props.dateInfo.data.time).format("YYYY-MM-DDT") + time).valueOf() +
                                    1000 * duration
                            )}
                        </div>
                    )}
                    <SimpleInput
                        name="newBookingReason"
                        text="Årsag"
                        id="newBookingReason"
                        multiline={true}
                        prop={booking.reason}
                        changeValue={value =>
                            setBooking(
                                update(booking, {
                                    reason: { $set: value }
                                })
                            )
                        }
                    />

                    <Button
                        variant="contained"
                        onClick={saveBooking}
                        style={{ margin: "10px 0 10px 0", fontFamily: "Lato" }}>
                        Gem
                    </Button>
                    {error !== "" ? (
                        <Typography color="secondary" variant="subtitle2" gutterBottom>
                            {error}
                        </Typography>
                    ) : null}

                    <div style={{ height: "40px" }}></div>
                </Container>
            </Drawer>
        </>
    );
};
export default EditBookingDrawer;
